import React from "react";
import axios from "axios";
import { CHART_URL } from "./constants.js";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./mmb.css";
import {
  convertDateToEST,
  convertDateToESTString,
  bufferToBase64,
  bufferToBase64Image,
  getHeaders,
  isMarketOpenOnDate,
  getTimezoneDiffWithEST,
} from "./utils.js";
import "./history.css";
import "./chart.css";
import EnlargeableImage from "./enlargeable-image.js";
import FitController, {
  measureHeight,
  measureTop,
  MIN_IMAGE_HEIGHT,
  FALLBACK_HEADER_HEIGHT,
} from "./fit-controller.js";

export default class Chart extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedDate: props.selectedDate,
      onDateChangeCallback: props.onDateChangeCallback,
      charts: [],
      symbol: "SPY",
      marketCalendar: props.marketCalendar,
      chartWidth: null,
      chartHeight: null,
    };

    this.containerRef = React.createRef();
    this.headerRef = React.createRef();
    this.fitRef = React.createRef();
    this.fit = new FitController({
      getSignature: () => this.getFitSignature(),
      applyFit: () => this.applyFit(),
    });

    this.getChart(/* initialization= */ true, this.state.symbol);
  }

  componentDidMount() {
    window.addEventListener("resize", this.handleResize);
    // The first fit can only run once the image has loaded, but the refs exist
    // now, so anything measured against the fallbacks is redone here.
    this.fit.reset();
    this.fit.check();
  }

  componentWillUnmount() {
    this.isUnmounted = true;
    this.fit.cancel();
    window.removeEventListener("resize", this.handleResize);
  }

  componentDidUpdate(prevProps) {
    if (this.props.selectedDate !== prevProps.selectedDate) {
      this.onDateChangeInternal(this.props.selectedDate);
    }

    this.fit.check();
  }

  handleResize = () => {
    this.fit.reset();
    this.fit.check();
  };

  /**
   * The chart is 1920x2000 -- taller than it is wide -- so left to itself at the
   * column's full width it runs several screens down the page. It is scaled to
   * whichever of the two axes runs out first, so the whole capture is on screen
   * at once and neither scrollbar ever appears.
   *
   * Measured rather than derived from the viewport: the width that matters is
   * the column's, not the window's, since the mmb and energy panes take their
   * share of the row first.
   */
  applyFit() {
    if (this.isUnmounted || !this.naturalWidth || !this.naturalHeight) {
      return;
    }

    const available = this.getAvailableSize();
    const ratio = Math.min(
      available.width / this.naturalWidth,
      available.height / this.naturalHeight
    );
    this.state.chartWidth = ratio * this.naturalWidth;
    this.state.chartHeight = ratio * this.naturalHeight;
    this.fit.markFitted();
    this.setState(this.state);
  }

  getFitSignature() {
    return (
      `${window.innerWidth}x${window.innerHeight}:${this.getColumnWidth()}:` +
      `${this.getFitTop()}`
    );
  }

  getHeaderHeight() {
    return measureHeight(this.headerRef, FALLBACK_HEADER_HEIGHT);
  }

  getContentTop() {
    return measureTop(this.containerRef);
  }

  // The column, not the window. It can shrink below the image's own width
  // because .chart-block sets min-width: 0, which is what stops a measurement
  // taken here from being one the oversized image itself produced.
  getColumnWidth() {
    return this.containerRef.current
      ? this.containerRef.current.getBoundingClientRect().width
      : window.innerWidth;
  }

  // Where the image itself starts, measured in one go rather than added up from
  // the column's top plus the header's height. Those two have to account for
  // every margin, border and fractional pixel between them to agree with where
  // the image actually lands, and whatever they miss is left as a strip of dead
  // space under the chart. The box's own top cannot be wrong about it.
  //
  // Viewport-relative on purpose: the question is how much room is left below
  // this point on screen, not how far down the document it sits.
  getFitTop() {
    return this.fitRef.current
      ? this.fitRef.current.getBoundingClientRect().top
      : this.getContentTop() + this.getHeaderHeight();
  }

  getAvailableSize() {
    return {
      width: this.getColumnWidth(),
      height: Math.max(window.innerHeight - this.getFitTop(), MIN_IMAGE_HEIGHT),
    };
  }

  handleImageLoad = (event) => {
    // Taken from the element that is already on the page rather than from a
    // second Image(): the fit then costs no extra request, and it re-runs by
    // itself whenever the date or symbol swaps in a differently sized capture.
    this.naturalWidth = event.target.naturalWidth;
    this.naturalHeight = event.target.naturalHeight;
    this.fit.reset();
    this.applyFit();
  };

  getChartUrl(symbol) {
    const { year, month, day } = convertDateToEST(this.state.selectedDate);
    return `${CHART_URL}/${symbol}/${year}/${month}/${day}`;
  }

  // A workaround to access local images via public internet. This requires to pass
  // ngrok headers. Shouldn't be run when in local network.
  getChart(initialization, symbol) {
    // Another day, or the other symbol, may not share the capture's dimensions.
    this.naturalWidth = null;
    this.naturalHeight = null;

    if (process.env.REACT_APP_ENV !== "prod-ngrok") {
      this.state.charts = [{ img: this.getChartUrl(symbol), error: null }];
      if (!initialization) {
        this.setState(this.state);
      }
      return;
    }

    this.state.charts = [{ img: null, error: null }];
    if (!initialization) {
      this.setState(this.state);
    }
    const promises = [];
    for (var i = 0; i < this.state.charts.length; i++) {
      const chart = this.state.charts[i];
      promises.push(this.getChartInternal(chart, symbol));
    }
    Promise.all(promises).then((res) => {
      this.setState(this.state);
    });
  }

  getChartInternal(chart, symbol) {
    return axios({
      method: "get",
      url: this.getChartUrl(symbol),
      headers: getHeaders(),
      params: { volume: chart.isVolume },
      responseType: "arraybuffer",
      withCredentials: true,
    })
      .then((res) => {
        chart.img = bufferToBase64Image(res.data);
      })
      .catch((err) => {
        if (err.response) {
          chart.error = `${err.message}. ${bufferToBase64(err.response.data)}`;
        } else {
          chart.error = `${err}. ${err.stack}`;
        }
      });
  }

  onDateChangeInternal(date) {
    this.state.selectedDate = date;
    this.getChart(/* initialization= */ false, this.state.symbol);
  }

  onDateChange(date) {
    if (this.state.onDateChangeCallback) {
      this.state.onDateChangeCallback(date);
    } else {
      this.onDateChangeInternal(date);
    }
  }

  isRetrievingChartImg(chart) {
    return !chart.img && !chart.error;
  }

  isRefreshButtonDisabled() {
    for (var i = 0; i < this.state.charts.length; i++) {
      if (this.isRetrievingChartImg(this.state.charts[i])) {
        return true;
      }
    }
    return false;
  }

  showOptions() {
    const { selectedDate, marketCalendar } = this.state;
    const dateInEST = convertDateToESTString(selectedDate);

    return (
      <div className="inline">
        <h2 className="inline"> Date:</h2>
        <div className="inline">
          <DatePicker
            className="date-picker"
            selected={new Date(selectedDate.getTime() + getTimezoneDiffWithEST())}
            value={dateInEST}
            onChange={(date) => this.onDateChange(date)}
            filterDate={(date) => {
              const dInEST = new Date(date.getTime() - getTimezoneDiffWithEST());
              return isMarketOpenOnDate(marketCalendar, dInEST);
            }}
          />
        </div>
        <h2 className="inline">
          (EST) {dateInEST}
        </h2>
        <button
          className="inline"
          disabled={this.isRefreshButtonDisabled()}
          onClick={() => this.getChart(/* initialization= */ false, this.state.symbol)}
        >
          Refresh
        </button>
        <button
          className="inline"
          onClick={() => {
            var symbol;
            if (this.state.symbol === "SPY") {
              symbol = "QQQ";
            } else {
              symbol = "SPY";
            }
            this.state.symbol = symbol;
            this.getChart(/* initialization= */ false, symbol);
          }}
        >
          {this.state.symbol}
        </button>
      </div>
    );
  }

  renderChart() {
    const options = [];
    for (var i = 0; i < this.state.charts.length; i++) {
      options.push(
        <div>
          {this.isRetrievingChartImg(this.state.charts[i]) && <img src={`${process.env.PUBLIC_URL}/loading.gif`} />}
          {this.state.charts[i].img && (
            <EnlargeableImage
              width={this.state.chartWidth}
              height={this.state.chartHeight}
              src={this.state.charts[i].img}
              onLoad={this.handleImageLoad}
            />
          )}
          {this.state.charts[i].error && (
            <div className="error-text">{this.state.charts[i].error}</div>
          )}
        </div>
      );
    }
    return (
      <div className="chart-fit" ref={this.fitRef}>
        {options}
      </div>
    );
  }

  render() {
    return (
      <div className="component-app" ref={this.containerRef}>
        <div className="header-bar" ref={this.headerRef}>
          {this.showOptions()}
        </div>
        {this.renderChart()}
      </div>
    );
  }
}
